import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { investorsAPI } from '@/lib/api-client'
import { formatDocument } from '@/lib/utils'
import { usePermission } from '@/hooks/usePermission'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { InvestorFormDialog } from './InvestorFormDialog'
import {
  ArrowLeft,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Landmark,
  Loader2,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react'

const ROLE_LABELS: Record<string, string> = {
  INVESTOR: 'Sócio Investidor',
  ADMINISTRATOR: 'Sócio Administrador',
}

const ROLE_COLORS: Record<string, string> = {
  INVESTOR: 'bg-blue-100 text-blue-700',
  ADMINISTRATOR: 'bg-purple-100 text-purple-700',
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

function formatDate(date: string) {
  return new Date(date).toLocaleDateString('pt-BR')
}

export function InvestorDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const canEdit = usePermission('investors:edit')
  const canDelete = usePermission('investors:delete')

  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const { data: investor, isLoading } = useQuery({
    queryKey: ['investor', id],
    queryFn: () => investorsAPI.getById(id!),
    enabled: !!id,
  })

  const { data: financialData } = useQuery({
    queryKey: ['investor', id, 'financial-summary'],
    queryFn: () => investorsAPI.getFinancialSummary(id!),
    enabled: !!id,
  })

  const deleteMutation = useMutation({
    mutationFn: () => investorsAPI.delete(id!),
    onSuccess: (data: any) => {
      toast.success(data.message)
      queryClient.invalidateQueries({ queryKey: ['investors'] })
      navigate('/investors')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!investor) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <h2 className="text-xl font-medium text-neutral-900">
          Investidor não encontrado
        </h2>
        <Button className="mt-4" onClick={() => navigate('/investors')}>
          Voltar
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/investors')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <Landmark className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold text-neutral-900">
                {investor.name}
              </h1>
              <Badge variant={investor.isActive ? 'default' : 'secondary'}>
                {investor.isActive ? 'Ativo' : 'Inativo'}
              </Badge>
              <Badge
                variant="outline"
                className={ROLE_COLORS[investor.role] || ''}
              >
                {ROLE_LABELS[investor.role] || investor.role}
              </Badge>
            </div>
            <p className="mt-1 text-neutral-500">{formatDocument(investor.document)}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canEdit && (
            <Button variant="outline" onClick={() => setShowEditDialog(true)}>
              <Edit className="mr-2 h-4 w-4" />
              Editar
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outline"
              className="text-red-600 hover:text-red-700"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Excluir
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Informações</TabsTrigger>
          <TabsTrigger value="financial">Financeiro</TabsTrigger>
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Dados Pessoais</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-neutral-500 w-24">Documento</span>
                <span className="text-sm text-neutral-900">{formatDocument(investor.document)}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-neutral-500 w-24">Tipo</span>
                <Badge
                  variant="outline"
                  className={ROLE_COLORS[investor.role] || ''}
                >
                  {ROLE_LABELS[investor.role] || investor.role}
                </Badge>
              </div>
              {investor.email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-neutral-400" />
                  <span className="text-sm text-neutral-900">{investor.email}</span>
                </div>
              )}
              {investor.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-neutral-400" />
                  <span className="text-sm text-neutral-900">{investor.phone}</span>
                </div>
              )}
              {investor.address && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-neutral-400" />
                  <span className="text-sm text-neutral-900">{investor.address}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-neutral-500">
                  Total Entradas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  <span className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(financialData?.totalEntradas || 0)}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-neutral-500">
                  Total Saídas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-red-600" />
                  <span className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(financialData?.totalSaidas || 0)}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-neutral-500">
                  Saldo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  {(financialData?.saldo || 0) >= 0 ? (
                    <ArrowUpRight className="h-5 w-5 text-green-600" />
                  ) : (
                    <ArrowDownRight className="h-5 w-5 text-red-600" />
                  )}
                  <span className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(financialData?.saldo || 0)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent transactions */}
          {financialData?.transacoesRecentes?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Transações Recentes</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Projeto</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {financialData.transacoesRecentes.map((tx: any) => (
                      <TableRow key={tx.id}>
                        <TableCell className="text-sm">
                          {formatDate(tx.date)}
                        </TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">
                          {tx.description}
                        </TableCell>
                        <TableCell className="text-sm">
                          {tx.project?.name || '-'}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">
                          <span className={tx.type === 'INCOME' ? 'text-green-600' : 'text-red-600'}>
                            {tx.type === 'INCOME' ? '+' : '-'}{formatCurrency(tx.amount)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      {showEditDialog && (
        <InvestorFormDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          investor={investor}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir Investidor</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o investidor{' '}
              <strong>{investor.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
